import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageHeader from "../../Ui_Components/PageHeader";
import StepIndicator from "./components/StepIndicator";
import BasicInfoStep from "./steps/BasicInfoStep";
import EducationWorkStep from "./steps/EducationWorkStep";
import RoleReportingStep from "./steps/RoleReportingStep";
import { DEFAULT_PERMISSIONS } from "./steps/RoleReportingStep";
import {
  addNewNutritionist,
  updateNutritionistDetails,
  assignRoleToNutritionist,
} from "../../../services/team";
import { ApiError } from "../../../services/http";
import { useAuth } from "../../../context/auth/AuthContext";
import { ToastContainer, useToast } from "../../Ui_Components/ToastMessage";
import {
  AddNutritionistFormData,
  BasicInfoErrors,
  EducationWorkErrors,
  PermissionsState,
  RoleReportingErrors,
  createId,
  initialBasicInfo,
  initialEducationWork,
  initialRoleReporting,
  validateBasicInfo,
  validateEducationWork,
  validateRoleReporting,
} from "./types";
import { TeamMember } from "../ChangeRoleModal";
import { NutritionistFullDetails } from "../ViewNutritionist/NutritionistViewPage";
import { Button } from "../../Ui_Components/Button";

const STEPS = [
  { number: 1, label: "Basic information" },
  { number: 2, label: "Education & Work" },
  { number: 3, label: "Role & reporting" },
];

/* Maps TeamMember.designation → role key used in the form */
const DESIGNATION_TO_ROLE: Record<string, string> = {
  "Jr Nutritionist":      "junior_nutritionist",
  "Junior Nutritionist":  "junior_nutritionist",
  "Senior Nutritionist":  "senior_nutritionist",
  "Sr nutritionist":      "senior_nutritionist",
  "Head Nutritionist":    "head_nutritionist",
  "Dietician":            "dietician",
  "Sports Nutritionist":  "sports_nutritionist",
  "Trainee":              "trainee",
};

const buildFormFromMember = (member: TeamMember): AddNutritionistFormData => {
  const nameParts = member.name.trim().split(" ");
  const role = DESIGNATION_TO_ROLE[member.designation] ?? "";
  const permissions = DEFAULT_PERMISSIONS[role] ?? { actions: {}, content: {} };
  return {
    basicInfo: {
      ...initialBasicInfo,
      firstName: nameParts[0] ?? "",
      lastName: nameParts.slice(1).join(" "),
    },
    educationWork: initialEducationWork,
    roleReporting: {
      ...initialRoleReporting,
      role,
      joiningDate: member.joiningDate,
      permissions,
    },
  };
};

/* Build the full form from a previously-fetched NutritionistFullDetails
   (passed in via location.state from the view page). Permissions are left
   empty here — RoleReportingStep auto-loads the role's permission map on
   mount whenever a role is already set. */
const buildFormFromDetails = (
  details: NutritionistFullDetails,
): AddNutritionistFormData => ({
  basicInfo: {
    photo: null,
    photoPreview: details.profileUrl,
    title: details.title.toLowerCase(),
    firstName: details.firstName,
    lastName: details.lastName,
    phone: details.phone,
    email: details.email,
    dob: details.dob,
    gender: details.gender.toLowerCase(),
    address: details.address,
    city: details.city,
    state: details.state,
    pincode: details.pincode,
    documents: details.documents.map((d) => ({
      id: createId(),
      type: d.type,
      number: d.number,
      file: null,
      fileName: d.uploadUrl
        ? decodeURIComponent(d.uploadUrl.split("/").pop() ?? "")
        : "",
    })),
  },
  educationWork: {
    qualifications:
      details.educations.length > 0
        ? details.educations.map((e) => ({
            id: createId(),
            qualification: e.qualification,
            collegeName: e.collegeName,
          }))
        : [{ id: createId(), qualification: "", collegeName: "" }],
    workExperience: details.workExperience,
    specialisations:
      details.specialisations.length > 0
        ? details.specialisations.map((s) => ({
            id: createId(),
            specialisation: s.specialisation,
            yearsInArea: s.experience,
          }))
        : [{ id: createId(), specialisation: "", yearsInArea: "" }],
    expertises:
      details.expertises.length > 0
        ? details.expertises.map((e) => ({ id: createId(), expertise: e }))
        : [{ id: createId(), expertise: "" }],
    certifications:
      details.certifications.length > 0
        ? details.certifications.map((c) => ({
            id: createId(),
            certification: c,
          }))
        : [{ id: createId(), certification: "" }],
    languages: [
      details.languages[0] ?? "",
      details.languages[1] ?? "",
      details.languages[2] ?? "",
      details.languages[3] ?? "",
    ],
  },
  roleReporting: {
    role: details.role,
    jobType: details.jobType,
    joiningDate: details.joiningDate,
    reportingManagerName: details.reportingManager,
    permissions: { actions: {}, content: {} },
  },
});

/* Build the multipart/form-data payload for ADD_NEW_NUTRITIONIST and
   UPDATE_NUTRITIONIST_DETAILS. Both endpoints accept the same field shape;
   only the URL differs. */
const buildNutritionistFormData = (form: AddNutritionistFormData): FormData => {
  const fd = new FormData();
  const { basicInfo, educationWork, roleReporting } = form;

  /* ── Basic info ── */
  /* Form stores title as lowercase ("mr"); API expects title-case ("Mr"). */
  if (basicInfo.title) {
    const t = basicInfo.title;
    fd.append("nutrist_title", t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
  }
  fd.append("nutrist_fname", basicInfo.firstName);
  fd.append("nutrist_lname", basicInfo.lastName);
  fd.append("nutrist_email", basicInfo.email);
  fd.append("nutrist_mobileno", basicInfo.phone);
  fd.append("nutrist_dob", basicInfo.dob);
  fd.append("nutrist_gender", basicInfo.gender);
  fd.append("nutrist_address", basicInfo.address);
  fd.append("nutrist_state", basicInfo.state);
  fd.append("nutrist_city", basicInfo.city);
  fd.append("nutrist_pincode", basicInfo.pincode);
  if (basicInfo.photo) fd.append("nutrist_profile", basicInfo.photo);

  /* Documents: doc_type / doc_no go as JSON-stringified arrays
     (e.g. ["License","Certificate"]). doc_upload stays as repeated
     file entries — that's the standard way to send multiple files in
     multipart/form-data. */
  const docTypes = basicInfo.documents.map((d) => d.type);
  const docNumbers = basicInfo.documents.map((d) => d.number);
  fd.append("doc_type", JSON.stringify(docTypes));
  fd.append("doc_no", JSON.stringify(docNumbers));
  basicInfo.documents.forEach((doc) => {
    if (doc.file) fd.append("doc_upload", doc.file);
  });

  /* ── Education & work ── */
  fd.append("nutrist_workexperience", educationWork.workExperience);

  const educationDetails = educationWork.qualifications
    .filter((q) => q.qualification || q.collegeName)
    .map((q) => ({
      education_qualification: q.qualification,
      education_clg_name: q.collegeName,
    }));
  fd.append("education_details", JSON.stringify(educationDetails));

  const specialisationData = educationWork.specialisations
    .filter((s) => s.specialisation || s.yearsInArea)
    .map((s) => ({
      specialisation: s.specialisation,
      spec_exp: s.yearsInArea,
    }));
  fd.append("specialisation_data", JSON.stringify(specialisationData));

  const expertiseData = educationWork.expertises
    .filter((e) => e.expertise)
    .map((e) => ({ expertise: e.expertise }));
  fd.append("expertise_data", JSON.stringify(expertiseData));

  const certificationData = educationWork.certifications
    .filter((c) => c.certification)
    .map((c) => ({ certification_name: c.certification }));
  fd.append("certification_data", JSON.stringify(certificationData));

  const [lang1, lang2, lang3, lang4] = educationWork.languages;
  if (lang1) fd.append("nutrist_lang1", lang1);
  if (lang2) fd.append("nutrist_lang2", lang2);
  if (lang3) fd.append("nutrist_lang3", lang3);
  if (lang4) fd.append("nutrist_lang4", lang4);

  /* ── Role & reporting ── */
  fd.append("nutrist_assign_role", roleReporting.role);
  fd.append("nutrist_job_type", roleReporting.jobType);
  fd.append("nutrist_joining_date", roleReporting.joiningDate);
  fd.append("nutrist_reporting_manager", roleReporting.reportingManagerName);

  return fd;
};

/* The two endpoints use different conventions for `success`: ADD/UPDATE
   returns the number 1, while ASSIGN_ROLE_TO_NUTRITIONIST returns the string
   "1". Accept either (plus `true`) so a server-side type drift doesn't
   silently break the sequence. */
const isSuccess = (v: unknown) =>
  v === 1 || v === "1" || v === true || v === "true";

/* Maps the server field names returned in {success:false, errors:{...}}
   payloads to the step number and form-state field they correspond to, so
   inline field errors can be surfaced on the same control the user filled in. */
type ServerFieldTarget =
  | { step: 1; key: keyof BasicInfoErrors }
  | { step: 2; key: "workExperience" | "languages" }
  | { step: 3; key: keyof RoleReportingErrors };

const SERVER_FIELD_MAP: Record<string, ServerFieldTarget> = {
  nutrist_title:            { step: 1, key: "title" },
  nutrist_fname:            { step: 1, key: "firstName" },
  nutrist_lname:            { step: 1, key: "lastName" },
  nutrist_email:            { step: 1, key: "email" },
  nutrist_mobileno:         { step: 1, key: "phone" },
  nutrist_dob:              { step: 1, key: "dob" },
  nutrist_gender:           { step: 1, key: "gender" },
  nutrist_address:          { step: 1, key: "address" },
  nutrist_state:            { step: 1, key: "state" },
  nutrist_city:             { step: 1, key: "city" },
  nutrist_pincode:          { step: 1, key: "pincode" },
  nutrist_workexperience:   { step: 2, key: "workExperience" },
  nutrist_lang1:            { step: 2, key: "languages" },
  nutrist_lang2:            { step: 2, key: "languages" },
  nutrist_lang3:            { step: 2, key: "languages" },
  nutrist_lang4:            { step: 2, key: "languages" },
  nutrist_assign_role:      { step: 3, key: "role" },
  nutrist_job_type:         { step: 3, key: "jobType" },
  nutrist_joining_date:     { step: 3, key: "joiningDate" },
  nutrist_reporting_manager:{ step: 3, key: "reportingManagerName" },
};

/* The backend returns each entry as either a plain string or an array of
   strings (DRF-style). Normalise to a single readable line. */
const flattenServerError = (val: unknown): string => {
  if (Array.isArray(val)) return val.filter(Boolean).join(" ");
  if (typeof val === "string") return val;
  if (val && typeof val === "object") {
    return Object.values(val as Record<string, unknown>)
      .map(flattenServerError)
      .filter(Boolean)
      .join(" ");
  }
  return val == null ? "" : String(val);
};

interface RoutedServerErrors {
  step1: BasicInfoErrors;
  step2: EducationWorkErrors;
  step3: RoleReportingErrors;
  /* Earliest step that received at least one error — used to jump the user
     back to the right place. Null if no field-level error could be routed. */
  firstStep: 1 | 2 | 3 | null;
  /* First specific message we recognised, suitable for the toast headline. */
  firstMessage: string;
  /* Field-level errors that didn't map to a known form field; surfaced
     verbatim in the page-level banner so they aren't silently dropped. */
  unmapped: string[];
}

const routeServerErrors = (
  errors: Record<string, unknown> | undefined,
): RoutedServerErrors => {
  const result: RoutedServerErrors = {
    step1: {},
    step2: {},
    step3: {},
    firstStep: null,
    firstMessage: "",
    unmapped: [],
  };
  if (!errors || typeof errors !== "object") return result;

  for (const [serverField, rawMsg] of Object.entries(errors)) {
    const message = flattenServerError(rawMsg);
    if (!message) continue;
    if (!result.firstMessage) result.firstMessage = message;

    const target = SERVER_FIELD_MAP[serverField];
    if (!target) {
      result.unmapped.push(`${serverField}: ${message}`);
      continue;
    }

    if (target.step === 1) {
      result.step1[target.key] = message;
    } else if (target.step === 2) {
      result.step2[target.key] = message;
    } else {
      result.step3[target.key] = message;
    }

    if (result.firstStep === null || target.step < result.firstStep) {
      result.firstStep = target.step;
    }
  }

  return result;
};

/* Flatten the form's permission state into the [{resource, action}] array
   shape the ASSIGN_ROLE_TO_NUTRITIONIST endpoint expects. */
const buildPermissionsPayload = (
  permissions: PermissionsState,
): { resource: string; action: string }[] => {
  const out: { resource: string; action: string }[] = [];
  Object.entries(permissions.actions).forEach(([key, enabled]) => {
    if (!enabled) return;
    const [resource, action] = key.split(":");
    if (resource && action) out.push({ resource, action });
  });
  Object.entries(permissions.content).forEach(([resource, ops]) => {
    (["create", "delete", "edit", "view"] as const).forEach((op) => {
      if (ops[op]) out.push({ resource, action: op });
    });
  });
  return out;
};

const AddNutritionistPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toasts, removeToast, error: showError } = useToast();

  const locationState = location.state as
    | { member?: TeamMember; mode?: string; details?: NutritionistFullDetails }
    | null;
  const editMember = locationState?.member;
  const editDetails = locationState?.details;
  const isEditMode = locationState?.mode === "edit" && !!editMember;

  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState<AddNutritionistFormData>(() => {
    if (isEditMode && editDetails) return buildFormFromDetails(editDetails);
    if (isEditMode && editMember) return buildFormFromMember(editMember);
    return {
      basicInfo: initialBasicInfo,
      educationWork: initialEducationWork,
      roleReporting: initialRoleReporting,
    };
  });

  const [step1Errors, setStep1Errors] = useState<BasicInfoErrors>({});
  const [step2Errors, setStep2Errors] = useState<EducationWorkErrors>({});
  const [step3Errors, setStep3Errors] = useState<RoleReportingErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /* Apply a server-returned errors map to the per-step error states, jump the
     user to the earliest failing step, and surface a toast + page banner so
     the failure is unmissable. Returns true if at least one error was
     recognised (the caller uses this to decide whether to fall back to the
     generic "Update failed" path). */
  const handleServerFieldErrors = (
    errors: Record<string, unknown>,
  ): boolean => {
    const routed = routeServerErrors(errors);
    const hasMapped = routed.firstStep !== null;

    if (hasMapped) {
      setStep1Errors(routed.step1);
      setStep2Errors(routed.step2);
      setStep3Errors(routed.step3);
      setCurrentStep(routed.firstStep as number);
    }

    const headline = isEditMode
      ? "Failed to update nutritionist"
      : "Failed to add nutritionist";
    const banner = [routed.firstMessage, ...routed.unmapped]
      .filter(Boolean)
      .join(" — ");

    if (banner) {
      setSubmitError(banner);
      showError(headline, routed.firstMessage || banner);
    } else {
      setSubmitError(headline);
      showError(headline);
    }

    return hasMapped || routed.unmapped.length > 0;
  };

  const handleSubmit = async () => {
    if (!user?.nutrist_id) {
      const msg = "Missing caller identity. Please sign in again.";
      setSubmitError(msg);
      showError("Cannot save", msg);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    /* Clear any prior server-driven field errors before resubmitting so a
       cleared field doesn't keep its old "already used" message. */
    setStep1Errors({});
    setStep2Errors({});
    setStep3Errors({});
    try {
      /* Step A: create or update the nutritionist record. */
      const body = buildNutritionistFormData(formData);
      const saveJson = isEditMode && editMember
        ? await updateNutritionistDetails(editMember.nutritionId, body)
        : await addNewNutritionist(body);
      console.log(
        `[AddNutritionist] ${isEditMode ? "UPDATE" : "ADD"} response`,
        saveJson,
      );

      if (!isSuccess(saveJson?.success)) {
        const serverErrors = (saveJson as { errors?: Record<string, unknown> })
          ?.errors;
        if (serverErrors && Object.keys(serverErrors).length > 0) {
          handleServerFieldErrors(serverErrors);
          return;
        }
        throw new Error(
          (saveJson as { message?: string })?.message ||
            `${isEditMode ? "Update" : "Create"} failed`,
        );
      }

      /* Step B: assign role + permissions using the id returned above
         (or the existing id in edit mode). The server has been observed to
         place nutrist_id at saveJson.data.nutrist_id; check a couple of
         fallback paths so a minor shape change doesn't break the chain. */
      const targetId =
        (saveJson as any)?.data?.nutrist_id ??
        (saveJson as any)?.nutrist_id ??
        (saveJson as any)?.data?.id ??
        (isEditMode ? editMember?.nutritionId : undefined);

      if (!targetId) {
        throw new Error(
          "Saved nutritionist but no nutrist_id was returned in the response.",
        );
      }

      const assignPayload: Record<string, unknown> = {
        target_nutrist_id: targetId,
        caller_id: user.nutrist_id,
      };
      if (formData.roleReporting.role) {
        assignPayload.role = formData.roleReporting.role;
      }
      const permList = buildPermissionsPayload(formData.roleReporting.permissions);
      if (permList.length > 0) assignPayload.permissions = permList;

      console.log("[AddNutritionist] ASSIGN payload", assignPayload);
      const assignJson = await assignRoleToNutritionist(assignPayload);
      console.log("[AddNutritionist] ASSIGN response", assignJson);

      if (!isSuccess(assignJson?.success)) {
        throw new Error(
          (assignJson as { message?: string })?.message ||
            `Role assignment failed`,
        );
      }

      navigate("/team", {
        state: {
          showSuccessToast: true,
          toastMessage: isEditMode
            ? "Nutritionist updated successfully"
            : "Nutritionist added successfully",
        },
      });
    } catch (err) {
      console.error("[AddNutritionist] submit failed", err);
      /* ApiError carries the parsed response body in `data`. The backend
         returns `{success:false, errors:{...}}` on validation failures
         (e.g. duplicate email on /update_nutritionist/), so try to route
         those into per-field errors before falling back to a generic toast. */
      if (err instanceof ApiError) {
        const data = err.data as
          | { errors?: Record<string, unknown> }
          | undefined;
        if (data?.errors && Object.keys(data.errors).length > 0) {
          if (handleServerFieldErrors(data.errors)) return;
        }
      }
      const msg = err instanceof Error ? err.message : "Failed to save nutritionist";
      setSubmitError(msg);
      showError(
        isEditMode ? "Failed to update nutritionist" : "Failed to add nutritionist",
        msg,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const errors = validateBasicInfo(formData.basicInfo);
      if (Object.keys(errors).length > 0) {
        setStep1Errors(errors);
        showError("Please fix the highlighted fields");
        return;
      }
      setStep1Errors({});
    }

    if (currentStep === 2) {
      const errors = validateEducationWork(formData.educationWork);
      if (Object.keys(errors).length > 0) {
        setStep2Errors(errors);
        showError("Please fix the highlighted fields");
        return;
      }
      setStep2Errors({});
    }

    if (currentStep === 3) {
      const errors = validateRoleReporting(formData.roleReporting);
      if (Object.keys(errors).length > 0) {
        setStep3Errors(errors);
        showError("Please fix the highlighted fields");
        return;
      }
      setStep3Errors({});
    }

    if (currentStep < 3) {
      setCurrentStep((s) => s + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleCancel = () => {
    if (isEditMode && editMember) {
      navigate(`/team/nutritionist/${editMember.id}`, { state: { member: editMember } });
    } else {
      navigate("/team");
    }
  };

  const backButton = currentStep > 1 && (
    <button
      type="button"
      onClick={handleBack}
      disabled={submitting}
      className="flex items-center gap-2 text-[#0DB5B5] text-sm 3xl:text-base font-medium font-inter hover:text-[#0CA0A0] transition-colors disabled:opacity-50"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      Back
    </button>
  );

  const cancelButton = (
    <Button
      variant="secondary"
      size="md"
      onClick={handleCancel}
      disabled={submitting}
    >
      Cancel
    </Button>
  );

  const rightButton =
    currentStep === 3 ? (
      <Button
        variant="primary"
        size="md"
        onClick={handleNext}
        disabled={submitting}
        startIcon={submitting ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : undefined}
      >
        {submitting
          ? isEditMode
            ? "Updating..."
            : "Saving..."
          : isEditMode
          ? "Update nutritionist"
          : "Save nutritionist"}
      </Button>
    ) : (
      <Button
        variant="primary"
        size="md"
        onClick={handleNext}
      >
        Save &amp; next
      </Button>
    );

  const pageTitle = isEditMode
    ? editMember?.name ?? "Edit nutritionist"
    : "Add nutritionist";

  const breadcrumb = isEditMode
    ? `Team > ${editMember?.name ?? "Edit nutritionist"}`
    : "Team > Add nutritionist";

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA]">
      {/* Header */}
      <PageHeader
        title={pageTitle}
        breadcrumb={breadcrumb}
        breadcrumbLinks={["/team"]}
      />

      {/* Step indicator */}
      <StepIndicator currentStep={currentStep} steps={STEPS} />

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 pb-4 custom-scrollbar-overlay">
        {submitError && (
          <div className="mt-3 mb-2 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm font-inter text-red-700">
            {submitError}
          </div>
        )}
        {/* All steps are mounted from the start so each step's dropdown / role
            permission fetches fire on page load. Inactive steps are hidden via
            CSS so the user sees an instant switch with no loading state when
            navigating between steps (important for edit mode). */}
        <div className={currentStep === 1 ? "" : "hidden"}>
          <BasicInfoStep
            data={formData.basicInfo}
            onChange={(basicInfo) => {
              setFormData((f) => ({ ...f, basicInfo }));
              if (Object.keys(step1Errors).length > 0)
                setStep1Errors(validateBasicInfo(basicInfo));
            }}
            errors={step1Errors}
            onError={showError}
          />
        </div>
        <div className={currentStep === 2 ? "" : "hidden"}>
          <EducationWorkStep
            data={formData.educationWork}
            onChange={(educationWork) => {
              setFormData((f) => ({ ...f, educationWork }));
              if (Object.keys(step2Errors).length > 0)
                setStep2Errors(validateEducationWork(educationWork));
            }}
            errors={step2Errors}
          />
        </div>
        <div className={currentStep === 3 ? "" : "hidden"}>
          <RoleReportingStep
            data={formData.roleReporting}
            onChange={(roleReporting) => {
              setFormData((f) => ({ ...f, roleReporting }));
              if (Object.keys(step3Errors).length > 0)
                setStep3Errors(validateRoleReporting(roleReporting));
            }}
            errors={step3Errors}
          />
        </div>
      </div>

      {/* Sticky footer */}
      <div className="flex-shrink-0 bg-white border-t border-[#E8E8E8] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
        <div>{backButton}</div>
        <div className="flex items-center gap-3">
          {cancelButton}
          {rightButton}
        </div>
      </div>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default AddNutritionistPage;
